import { Link } from "react-router-dom";
import { ArrowUpRight, Linkedin, PenLine } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { Reveal, SectionHead } from "./Reveal";

export const NoteCard = ({ n, lang, k, testid }) => (
  <Link
    to={`/knowledge/notes/${n.slug}${lang === "en" ? "" : `?lang=${lang}`}`}
    data-testid={testid}
    className="group flex h-full flex-col border border-[#f8f7f4]/15 bg-[#f8f7f4]/[0.04] p-6 sm:p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[#e63946]/60 hover:bg-[#f8f7f4]/[0.07]"
  >
    <div className="flex items-center justify-between gap-3 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#f8f7f4]/50">
      <span className="flex items-center gap-2"><PenLine className="h-3.5 w-3.5 text-[#e63946]" /> {k.notes.kind}</span>
      <time dateTime={n.updated}>{n.updated}</time>
    </div>
    <h3 className="mt-4 font-display text-lg sm:text-xl font-bold leading-snug tracking-tight text-[#f8f7f4] group-hover:text-[#e63946] transition-colors duration-200">{n.title}</h3>
    <p className="mt-3 flex-1 text-sm leading-relaxed text-[#f8f7f4]/60">{n.summary}</p>
    <span className="mt-5 inline-flex items-center gap-2 font-mono-tech text-[10.5px] uppercase tracking-[0.15em] text-[#f8f7f4]/80">
      {k.notes.read} <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </span>
  </Link>
);

export const FieldNotes = ({ notes }) => {
  const { lang, t } = useLang();
  const k = t.knowledge;
  return (
    <section id="notes" className="relative bg-ink bp-grid-dark border-y border-[#f8f7f4]/10 py-20 sm:py-28" data-testid="notes-section">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <SectionHead dark eyebrow={k.notes.eyebrow} title={k.notes.title} desc={k.notes.desc} testid="notes" />
          </div>
          <Reveal delay={0.1} className="lg:col-span-4">
            <div className="flex items-center gap-4 border border-[#f8f7f4]/15 p-4">
              <img src="/assets/img/founder-gregory-studio.jpg" alt="Gregory Radek" width={56} height={56} loading="lazy" className="h-14 w-14 shrink-0 object-cover grayscale" />
              <div className="min-w-0">
                <p className="font-mono-tech text-[10px] uppercase tracking-[0.15em] text-[#f8f7f4]/60" data-testid="notes-byline">{k.notes.byline}</p>
                <a href="https://www.linkedin.com/in/gregradek/" target="_blank" rel="noopener noreferrer me" data-testid="notes-linkedin" className="mt-1.5 inline-flex items-center gap-1.5 font-mono-tech text-[10.5px] uppercase tracking-[0.15em] text-[#e63946] hover:text-[#f8f7f4] transition-colors">
                  <Linkedin className="h-3.5 w-3.5" /> {k.notes.linkedin}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="notes-grid">
          {(notes || []).map((n, i) => (
            <Reveal key={n.slug} delay={i * 0.08}>
              <NoteCard n={n} lang={lang} k={k} testid={`note-card-${n.slug}`} />
            </Reveal>
          ))}
          {!notes && [0, 1].map((i) => <div key={i} className="h-56 animate-pulse border border-[#f8f7f4]/10 bg-[#f8f7f4]/5" />)}
        </div>
      </div>
    </section>
  );
};
